import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CardUI } from './CardUI';
import { useGameBoard } from './GameBoardContext';

export const CaptureAnimationOverlay: React.FC = () => {
  const { capturingCards, gameState } = useGameBoard();
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1000,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  interface CardPosition {
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
  }

  const [positions, setPositions] = useState<Record<string, CardPosition>>({});
  const [isReady, setIsReady] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useLayoutEffect(() => {
    if (!capturingCards) {
      setPositions({});
      setIsReady(false);
      return;
    }

    const { cards, playedCard, playerIndex } = capturingCards;
    const numPlayers = gameState.numPlayers;
    const allCards = [...cards, playedCard];
    const newPositions: Record<string, CardPosition> = {};

    // 1. Calculate target pile coordinate
    const width = window.innerWidth;
    const height = window.innerHeight;
    let targetX = width / 2;
    let targetY = height / 2;

    if (playerIndex === 0) {
      const el = document.getElementById('prese-pile-0');
      if (el) {
        const rect = el.getBoundingClientRect();
        targetX = rect.left + rect.width / 2;
        targetY = rect.top + rect.height / 2;
      } else {
        targetX = width - 80;
        targetY = height - 100;
      }
    } else {
      const el = document.getElementById(`opponent-hand-${playerIndex}`);
      if (el) {
        const rect = el.getBoundingClientRect();
        targetX = rect.left + rect.width / 2;
        targetY = rect.top + rect.height / 2;
      } else {
        // Fallback layout mappings
        if (numPlayers === 4) {
          if (playerIndex === 1) { targetX = width - 60; targetY = height / 2; }
          else if (playerIndex === 2) { targetX = width / 2; targetY = 70; }
          else if (playerIndex === 3) { targetX = 60; targetY = height / 2; }
        } else if (numPlayers === 3) {
          if (playerIndex === 1) { targetX = width / 2; targetY = 70; }
          else if (playerIndex === 2) { targetX = 60; targetY = height / 2; }
        } else {
          if (playerIndex === 1) { targetX = width / 2; targetY = 70; }
        }
      }
    }

    // 2. Measure starting positions of all cards
    allCards.forEach(card => {
      const el = document.getElementById(card.id);
      let startX = width / 2;
      let startY = height / 2;

      if (el) {
        const rect = el.getBoundingClientRect();
        startX = rect.left + rect.width / 2;
        startY = rect.top + rect.height / 2;
      } else {
        // Fallback: if we couldn't find the element, place it near the center with a slight offset
        const isPlayed = card.id === playedCard.id;
        startX = width / 2 + (isPlayed ? 0 : 50);
        startY = height / 2 + (isPlayed ? 30 : -30);
      }

      newPositions[card.id] = {
        startX,
        startY,
        targetX,
        targetY
      };
    });

    setPositions(newPositions);
    setIsReady(true);
  }, [capturingCards, gameState.numPlayers]);

  if (!capturingCards || !isReady) return null;

  const { cards, playedCard } = capturingCards;
  const allCards = [...cards, playedCard];

  return (
    <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
      <AnimatePresence>
        {allCards.map((card, i) => {
          const pos = positions[card.id];
          if (!pos) return null;

          const total = allCards.length;
          const isPlayed = card.id === playedCard.id;

          // Tilts for starting and landing
          const startRotation = isPlayed ? 0 : (i - (total - 2) / 2) * 8;
          const targetRotation = (Math.random() - 0.5) * 45; // slight random landing rotation

          // Staggered flight delay for cascading effect
          const delay = i * 0.08;

          return (
            <motion.div
              key={`${card.id}-capture-overlay`}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                x: pos.startX,
                y: pos.startY,
                translateX: "-50%",
                translateY: "-50%",
                transformOrigin: 'center center',
              }}
              animate={{
                x: [pos.startX, dimensions.width / 2, pos.targetX],
                y: [pos.startY, dimensions.height / 2, pos.targetY],
                scale: [1, 1.15, 0.35],
                rotate: [startRotation, 0, targetRotation],
                opacity: [1, 1, 0],
              }}
              transition={{
                duration: 0.85,
                ease: "easeInOut",
                delay: delay,
                times: [0, 0.25, 1] // 0-25% gather at center, 25-100% fly to target
              }}
              className="relative"
            >
              {/* Highlight Glow during initial phase */}
              <div className="relative">
                <div className="absolute -inset-1 rounded-lg bg-amber-400/40 blur-md animate-pulse opacity-85" />
                <CardUI card={card} disabled scale={0.8} />
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
